import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import aboutUsMain from '../images/about_us_main.webp';
import whoWeAreImage from '../images/who_we_are_image.webp';
import ourProgress from '../images/OUR_PROGRESS.webp';
import grpPeople from '../images/grp_people.webp';

const AboutUs = ({ onHomeClick, onConnectClick, onCourseSelect }) => {
  const heroRef = useRef(null);
  const [activeFAQ, setActiveFAQ] = useState(null);

  useEffect(() => {
    // Scroll to top on mount
    if (heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Disable scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Intersection Observer for fade-in animations
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-section');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    document.querySelectorAll('.fade-section').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const faqs = [
    {
      question: "What makes Oranium Tech different from other training institutes?",
      answer: "We offer 100% placement support with partnerships across 100+ companies, industry-expert trainers with 10+ years of experience, and real-time project-based learning. Our training is aligned with current industry demands."
    },
    {
      question: "Do you provide placement assistance?",
      answer: "Yes! We provide 100% placement support including resume building, interview preparation, mock interviews, and direct connections with our hiring partners including TCS, Wipro, Cognizant, Infosys, and many more."
    },
    {
      question: "Are the courses suitable for beginners?",
      answer: "Absolutely! Our courses are designed for all skill levels - from complete beginners to advanced professionals looking to upskill. We start with fundamentals and gradually progress to advanced concepts."
    },
    {
      question: "What is the duration of the courses?",
      answer: "Course duration varies from 2 to 6 months depending on the program. We offer flexible schedules including weekend batches and online training options to accommodate working professionals."
    },
    {
      question: "Do you provide course completion certificates?",
      answer: "Yes, we provide industry-recognized course completion certificates. Additionally, we guide students for relevant certification exams like AWS, Azure, Selenium, and more."
    }
  ];

  return (
    <div ref={heroRef} style={{ backgroundColor: '#ffffff', paddingTop: '0' }}>
      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-in-section {
          animation: fadeInUp 1s ease-out forwards;
        }

        .fade-section {
          opacity: 0;
        }

        @keyframes glossyShine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(200%) translateY(200%) rotate(45deg);
          }
        }

        @keyframes continuousShine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(200%) translateY(200%) rotate(45deg);
          }
        }

        .feature-box .glossy-shine {
          animation: continuousShine 3s ease-in-out infinite !important;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes floatBubble {
          0%, 100% {
            transform: translate(-50%, -50%) translate(0, 0);
          }
          25% {
            transform: translate(-50%, -50%) translate(20px, -15px);
          }
          50% {
            transform: translate(-50%, -50%) translate(-15px, -25px);
          }
          75% {
            transform: translate(-50%, -50%) translate(15px, -10px);
          }
        }

        @keyframes floatImage {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes morphBubble {
          0%, 100% {
            border-radius: 50% 50% 50% 50%;
            transform: translate(-50%, -50%) rotate(0deg);
          }
          25% {
            border-radius: 45% 55% 50% 50%;
            transform: translate(-50%, -50%) rotate(90deg);
          }
          50% {
            border-radius: 50% 50% 45% 55%;
            transform: translate(-50%, -50%) rotate(180deg);
          }
          75% {
            border-radius: 55% 45% 50% 50%;
            transform: translate(-50%, -50%) rotate(270deg);
          }
        }

        @keyframes gradientMove {
          0% { background-position: 0% 0%; }
          100% { background-position: 0% 100%; }
        }

        @media (max-width: 1024px) {
          .features-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }
        }

        @media (max-width: 968px) {
          .grid-section {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          .grid-section > div:first-child {
            order: 2;
            min-height: 300px !important;
            padding: 2rem 0 !important;
          }
          .grid-section > div:last-child {
            order: 1;
          }
          .progress-section .grid-section > div:first-child {
            order: 1;
          }
          .progress-section .grid-section > div:last-child {
            order: 2;
          }
        }

        @media (max-width: 768px) {
          .hero-section {
            padding: 1.5rem !important;
            height: auto !important;
            min-height: 60vh !important;
            overflow: visible !important;
            box-sizing: border-box !important;
          }
          .hero-section > div:first-child {
            background-position: 60% center !important;
          }
          .hero-section > div:nth-child(2) {
            background: rgba(0, 0, 0, 0.6) !important;
          }
          .diagonal-blue-frame {
            display: none !important;
          }
          .hero-section > div:nth-child(4) {
            padding: 22rem 0 2rem !important;
            display: flex !important;
            align-items: flex-start !important;
            height: auto !important;
            min-height: auto !important;
          }
          .hero-section > div:nth-child(4) > div {
            margin-top: 3rem !important;
          }
          .hero-section h1 {
            font-size: 2rem !important;
            margin-bottom: 1rem !important;
          }
          .hero-section p {
            font-size: 1rem !important;
            text-align: justify !important;
          }
          .content-section {
            padding: 2.5rem 1.5rem !important;
            overflow: visible !important;
          }
          .content-section p {
            text-align: justify !important;
          }
          .content-section h2 {
            white-space: nowrap !important;
          }
          .content-section:last-of-type h2 {
            white-space: normal !important;
            font-size: 1.6rem !important;
            line-height: 1.3 !important;
          }
          .success-grid {
            gap: 1rem !important;
            padding: 0 !important;
          }
          .success-grid + div {
            padding: 2rem 0 !important;
          }
          .success-grid + div > div:first-child,
          .success-grid + div > div:last-child {
            padding: 1.5rem 1rem !important;
          }
          .success-grid + div > div > div {
            padding-left: 0 !important;
            border-left: none !important;
          }
          .success-grid + div > div > div h3 {
            text-align: left !important;
          }
          .success-grid + div > div > div p {
            text-align: left !important;
          }
          .stat-grid {
            grid-template-columns: 1fr !important;
            gap: 1.2rem !important;
          }
          .features-grid {
            gap: 1.2rem !important;
          }
          .success-box {
            margin: 0.5rem 0 !important;
          }
        }

        @media (max-width: 480px) {
          .hero-section {
            padding: 1rem !important;
            height: auto !important;
            min-height: 80vh !important;
            overflow: visible !important;
            box-sizing: border-box !important;
          }
          .hero-section > div:nth-child(4) {
            padding: 10rem 0 1.5rem !important;
            min-height: auto !important;
          }
          .hero-section h1 {
            font-size: 1.5rem !important;
            margin-bottom: 0.8rem !important;
          }
          .hero-section p {
            font-size: 0.9rem !important;
          }
          .hero-section button {
            padding: 0.8rem 1.8rem !important;
            font-size: 0.95rem !important;
          }
          .content-section {
            padding: 2rem 1rem !important;
          }
          .content-section:last-of-type h2 {
            font-size: 1.4rem !important;
          }
          .success-grid + div {
            padding: 2rem 0 !important;
          }
          .success-grid + div > div:first-child,
          .success-grid + div > div:last-child {
            padding: 1.5rem 0.5rem !important;
          }
        }
      `}</style>

      {/* Section 1: Hero Section with Image */}
      <section 
        className="hero-section"
        style={{
          position: 'relative',
          minHeight: '700px',
          marginTop:'-170px',
          overflow: 'hidden'
        }}
      >
        {/* Background Image */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${aboutUsMain})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
          backgroundRepeat: 'no-repeat',
          zIndex: 1,
          willChange: 'transform'
        }}></div>

        {/* Black Low Intensity Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1
        }}></div>

        {/* Blue Transparent Frame - Diagonal Left Half */}
        <div className="diagonal-blue-frame" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          clipPath: 'polygon(0 0, 60% 0, 40% 100%, 0 100%)',
          background: 'rgba(0, 31, 63, 0.5)',
          zIndex: 2
        }}></div>

        {/* Content */}
        <div style={{
          position: 'relative',
          zIndex: 3,
          padding: '12rem 2rem 3rem',
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          minHeight: '380px'
        }}>
          <div style={{
            maxWidth: '600px',
            color: 'white'
          }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '800',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              textShadow: '3px 3px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 0, 0, 0.5)'
            }}>
              About Us
            </h1>
            <p style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.25rem)',
              lineHeight: '1.8',
              marginBottom: '2.5rem',
              textShadow: '2px 2px 6px rgba(0, 0, 0, 0.8), 0 0 15px rgba(0, 0, 0, 0.5)'
            }}>
              Empowering learners with real skills, real projects, and real confidence through practical IT training, expert mentorship, and complete career support.
            </p>
            <button
              onClick={onConnectClick}
              style={{
                background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%)',
                color: 'white',
                padding: '1rem 2.5rem',
                border: 'none',
                borderRadius: '30px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 8px 25px rgba(255, 107, 53, 0.4)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 15px 35px rgba(255, 107, 53, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 25px rgba(255, 107, 53, 0.4)';
              }}
            >
              Connect With Us
            </button>
          </div>
        </div>
      </section>

      {/* Section 2: Who We Are */}
      <section className="content-section" style={{ padding: '2rem 2rem', background: '#f0f7ff' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div className="grid-section" style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.5fr',
            gap: '4rem',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
              {/* Morphing Animated Bubble */}
              <div style={{
                position: 'absolute',
                width: '300px',
                height: '300px',
                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, rgba(59, 130, 246, 0.2) 50%, rgba(59, 130, 246, 0.05) 100%)',
                zIndex: 0,
                animation: 'morphBubble 8s ease-in-out infinite',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                filter: 'blur(1px)'
              }}></div>
              <img 
                src={whoWeAreImage} 
                alt="IT training institute team at Oranium Tech Chennai"
                style={{
                  width: '50%',
                  height: 'auto',
                  borderRadius: '0',
                  boxShadow: 'none',
                  position: 'relative',
                  zIndex: 1,
                  animation: 'floatImage 3s ease-in-out infinite'
                }}
              />
            </div>
            <div>
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                color: '#001f3f',
                marginBottom: '0.5rem',
                fontWeight: '700',
                position: 'relative',
                paddingBottom: '0.5rem'
              }}>
                Who We Are
                <span style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100px',
                  height: '4px',
                  background: 'linear-gradient(90deg, #ff6b35 0%, #ff8c42 100%)',
                  borderRadius: '2px'
                }}></span>
              </h2>
              <p style={{
                fontSize: '1.05rem',
                color: '#4b5563',
                lineHeight: '1.8',
                marginBottom: '1.2rem',
                marginTop: '1.5rem',
                fontWeight: '500',
                textAlign: 'justify'
              }}>
                Oranium Tech is one of Chennai's most trusted and fast-growing IT training institutes, committed to bridging the gap between academic learning and real industry needs. Our goal is simple — to help individuals build strong, practical, job-ready IT skills that can immediately add value in the workplace.
              </p>
              <p style={{
                fontSize: '1.05rem',
                color: '#4b5563',
                lineHeight: '1.8',
                marginBottom: '1.2rem',
                fontWeight: '500',
                textAlign: 'justify'
              }}>
                We deliver high-quality training in the most in-demand technologies such as <span onClick={() => onCourseSelect && onCourseSelect('Artificial Intelligence & Gen-AI')} style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}>Artificial Intelligence</span>, <span onClick={() => onCourseSelect && onCourseSelect('Cloud Computing')} style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}>Cloud Computing</span>, <span onClick={() => onCourseSelect && onCourseSelect('DevOps & SRE')} style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}>DevOps</span>, <span onClick={() => onCourseSelect && onCourseSelect('Full-Stack Development')} style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}>Full-Stack Development</span>, <span onClick={() => onCourseSelect && onCourseSelect('Data Analytics')} style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}>Data Analytics</span>, and <span onClick={() => onCourseSelect && onCourseSelect('Software Testing')} style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}>Software Testing</span>. Every course is designed with hands-on practice, real-time projects, and personalised guidance, ensuring students learn the way companies expect.
              </p>
              <p style={{
                fontSize: '1.05rem',
                color: '#4b5563',
                lineHeight: '1.8',
                marginBottom: '1.2rem',
                fontWeight: '500',
                textAlign: 'justify'
              }}>
                With a team of experienced industry professionals, Oranium Tech follows a practical-first teaching approach — no theoretical overload, only real skills that matter in interviews and on the job.
              </p>
              <p style={{
                fontSize: '1.05rem',
                color: '#4b5563',
                lineHeight: '1.8',
                marginBottom: '1.2rem',
                fontWeight: '500',
                textAlign: 'justify'
              }}>
                Based in Chennai, with training centres in <span style={{ color: '#ff6b35', fontWeight: '600' }}>Chromepet</span> and <span style={{ color: '#ff6b35', fontWeight: '600' }}>Velachery</span>, we have successfully trained <span style={{ color: '#ff6b35', fontWeight: '600' }}>10,000+ students</span> and supported them in securing opportunities across top IT companies and growing startups.
              </p>
              <p style={{
                fontSize: '1.05rem',
                color: '#4b5563',
                lineHeight: '1.8',
                marginBottom: '0',
                fontWeight: '500',
                textAlign: 'justify'
              }}>
                At Oranium Tech, we don't just offer training — <span style={{ color: '#ff6b35', fontWeight: '600' }}>we create careers</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Ratio Section */}
      <section className="content-section fade-section" style={{
        padding: '2rem 2rem 3rem',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        overflow: 'visible'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 2.8rem)',
            color: '#001f3f',
            marginBottom: '2.5rem',
            fontWeight: '700'
          }}>
            Our Success Ratio
          </h2>
          <div className="success-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            padding: '1rem 0'
          }}>
            <div className="success-box" style={{
              background: '#001f3f',
              padding: '2.5rem',
              borderRadius: '15px',
              boxShadow: '0 15px 40px rgba(0, 31, 63, 0.25)',
              border: 'none',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '176px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 31, 63, 0.3)';
              const overlay = e.currentTarget.querySelector('.hover-overlay');
              if (overlay) overlay.style.transform = 'scale(20)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 31, 63, 0.25)';
              const overlay = e.currentTarget.querySelector('.hover-overlay');
              if (overlay) overlay.style.transform = 'scale(0)';
            }}
            >
              <div className="hover-overlay" style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255, 107, 53, 0.3) 0%, rgba(255, 140, 66, 0.2) 100%)',
                transform: 'translate(-50%, -50%) scale(0)',
                transition: 'transform 0.6s ease',
                pointerEvents: 'none',
                zIndex: 0
              }}></div>
              <div style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.2rem)',
                fontWeight: '800',
                color: 'white',
                marginBottom: '0.8rem',
                position: 'relative',
                zIndex: 1
              }}>
                28%
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.05rem',
                margin: 0,
                lineHeight: '1.5',
                position: 'relative',
                zIndex: 1
              }}>
                Of students are employed before graduating
              </p>
            </div>

            <div className="success-box" style={{
              background: '#001f3f',
              padding: '2.5rem',
              borderRadius: '15px',
              boxShadow: '0 15px 40px rgba(0, 31, 63, 0.25)',
              border: 'none',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '176px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 31, 63, 0.3)';
              const overlay = e.currentTarget.querySelector('.hover-overlay');
              if (overlay) overlay.style.transform = 'scale(20)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 31, 63, 0.25)';
              const overlay = e.currentTarget.querySelector('.hover-overlay');
              if (overlay) overlay.style.transform = 'scale(0)';
            }}
            >
              <div className="hover-overlay" style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255, 107, 53, 0.3) 0%, rgba(255, 140, 66, 0.2) 100%)',
                transform: 'translate(-50%, -50%) scale(0)',
                transition: 'transform 0.6s ease',
                pointerEvents: 'none',
                zIndex: 0
              }}></div>
              <div style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.2rem)',
                fontWeight: '800',
                color: 'white',
                marginBottom: '0.8rem',
                position: 'relative',
                zIndex: 1
              }}>
                83%
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.05rem',
                margin: 0,
                lineHeight: '1.5',
                position: 'relative',
                zIndex: 1
              }}>
                of grads are employed under 6 months
              </p>
            </div>

            <div className="success-box" style={{
              background: '#001f3f',
              padding: '2.5rem',
              borderRadius: '15px',
              boxShadow: '0 15px 40px rgba(0, 31, 63, 0.25)',
              border: 'none',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '176px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 31, 63, 0.3)';
              const overlay = e.currentTarget.querySelector('.hover-overlay');
              if (overlay) overlay.style.transform = 'scale(20)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 31, 63, 0.25)';
              const overlay = e.currentTarget.querySelector('.hover-overlay');
              if (overlay) overlay.style.transform = 'scale(0)';
            }}
            >
              <div className="hover-overlay" style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255, 107, 53, 0.3) 0%, rgba(255, 140, 66, 0.2) 100%)',
                transform: 'translate(-50%, -50%) scale(0)',
                transition: 'transform 0.6s ease',
                pointerEvents: 'none',
                zIndex: 0
              }}></div>
              <div style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.2rem)',
                fontWeight: '800',
                color: 'white',
                marginBottom: '0.8rem',
                position: 'relative',
                zIndex: 1
              }}>
                96%
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.05rem',
                margin: 0,
                lineHeight: '1.5',
                position: 'relative',
                zIndex: 1
              }}>
                of grads are employed 9 months or less
              </p>
            </div>

            <div className="success-box" style={{
              background: '#001f3f',
              padding: '2.5rem',
              borderRadius: '15px',
              boxShadow: '0 15px 40px rgba(0, 31, 63, 0.25)',
              border: 'none',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '176px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 31, 63, 0.3)';
              const overlay = e.currentTarget.querySelector('.hover-overlay');
              if (overlay) overlay.style.transform = 'scale(20)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 31, 63, 0.25)';
              const overlay = e.currentTarget.querySelector('.hover-overlay');
              if (overlay) overlay.style.transform = 'scale(0)';
            }}
            >
              <div className="hover-overlay" style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255, 107, 53, 0.3) 0%, rgba(255, 140, 66, 0.2) 100%)',
                transform: 'translate(-50%, -50%) scale(0)',
                transition: 'transform 0.6s ease',
                pointerEvents: 'none',
                zIndex: 0
              }}></div>
              <div style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.2rem)',
                fontWeight: '800',
                color: 'white',
                marginBottom: '0.8rem',
                position: 'relative',
                zIndex: 1
              }}>
                98%
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.05rem',
                margin: 0,
                lineHeight: '1.5',
                position: 'relative',
                zIndex: 1
              }}>
                of grads are employed 12 months or less
              </p>
            </div>
          </div>

          {/* Mission & Vision - Enhanced Layout */}
          <div style={{
            marginTop: '3rem',
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(96, 165, 250, 0.05) 100%)',
            borderRadius: '24px',
            padding: '3rem 2rem',
            boxShadow: '0 10px 40px rgba(59, 130, 246, 0.08)'
          }}>
            {/* Decorative Elements */}
            <div style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '100px',
              height: '100px',
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(20px)'
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '20px',
              left: '20px',
              width: '120px',
              height: '120px',
              background: 'radial-gradient(circle, rgba(96, 165, 250, 0.12) 0%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(25px)'
            }}></div>
            
            {/* Split Layout */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '0',
              position: 'relative',
              alignItems: 'start'
            }}>
              {/* Mission - Left Side */}
              <div style={{
                padding: '3rem 2.5rem',
                position: 'relative',
                background: 'transparent'
              }}>
                <div style={{
                  position: 'relative',
                  paddingLeft: '2rem',
                  borderLeft: '4px solid #3b82f6',
                  height: '100%'
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '1.5rem'
                  }}>
                    <h3 style={{
                      fontSize: 'clamp(1.8rem, 3vw, 2.2rem)',
                      color: '#001f3f',
                      margin: 0,
                      fontWeight: '700',
                      letterSpacing: '-0.5px'
                    }}>
                      Our Mission
                    </h3>
                  </div>
                  
                  <p style={{
                    color: '#1e293b',
                    fontSize: 'clamp(1rem, 2vw, 1.15rem)',
                    lineHeight: '1.9',
                    margin: 0,
                    fontWeight: '500',
                    textAlign: 'justify'
                  }}>
                    To give every learner <strong style={{color: '#ff6b35', fontWeight: '700'}}>real knowledge, real confidence, and real results</strong>. We focus on practical training, personal attention, and industry-level skills that help students start strong and grow faster in their IT career. <strong style={{color: '#ff6b35', fontWeight: '700'}}>Quality first. Commitment always.</strong>
                  </p>
                </div>
              </div>



              {/* Vision - Right Side */}
              <div style={{
                padding: '3rem 2.5rem',
                position: 'relative',
                background: 'transparent'
              }}>
                <div style={{
                  position: 'relative',
                  paddingLeft: '2rem',
                  borderLeft: '4px solid #60a5fa',
                  height: '100%'
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '1.5rem'
                  }}>
                    <h3 style={{
                      fontSize: 'clamp(1.8rem, 3vw, 2.2rem)',
                      color: '#001f3f',
                      margin: 0,
                      fontWeight: '700',
                      letterSpacing: '-0.5px'
                    }}>
                      Our Vision
                    </h3>
                  </div>
                  
                  <p style={{
                    color: '#1e293b',
                    fontSize: 'clamp(1rem, 2vw, 1.15rem)',
                    lineHeight: '1.9',
                    margin: 0,
                    fontWeight: '500',
                    textAlign: 'justify'
                  }}>
                    Our vision is to build <strong style={{color: '#ff8c42', fontWeight: '700'}}>India's most trusted and result-driven IT training institute</strong> — a place where students don't just learn… they transform. We aim to create skilled, confident, and job-ready professionals by delivering training that matches real industry needs and future technology trends.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Our Features */}
      <section className="content-section fade-section" style={{
        padding: '3rem 2rem',
        position: 'relative',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url(${ourProgress})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h2 style={{
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            color: 'white',
            marginBottom: '0.8rem',
            textAlign: 'center',
            fontWeight: '700'
          }}>
            Our Features
          </h2>
          <p style={{
            fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.7',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 2rem',
            fontWeight: '400'
          }}>
            Empowering students with industry-leading training, expert mentorship, and
            comprehensive placement support to build successful careers in technology
          </p>

          <div className="features-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1.5rem',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            <div className="feature-box" style={{
              background: 'linear-gradient(135deg, rgba(0, 61, 122, 0.95) 0%, rgba(0, 49, 93, 0.95) 100%)',
              padding: '1.8rem 1.5rem',
              borderRadius: '16px',
              minHeight: '130px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.25)';
              const shine = e.currentTarget.querySelector('.glossy-shine');
              if (shine) shine.style.animation = 'glossyShine 0.8s ease-out';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
              const shine = e.currentTarget.querySelector('.glossy-shine');
              if (shine) shine.style.animation = 'none';
            }}
            >
              <div className="glossy-shine" style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '50px',
                height: '200%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                pointerEvents: 'none',
                zIndex: 2
              }}></div>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.08) 100%)',
                pointerEvents: 'none'
              }}></div>
              <h3 style={{
                color: 'white',
                fontSize: 'clamp(1.1rem, 2.5vw, 1.25rem)',
                fontWeight: '700',
                marginBottom: '0.7rem',
                position: 'relative',
                zIndex: 1
              }}>
                Industry Expert Trainers
              </h3>
              <p style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                lineHeight: '1.5',
                margin: 0,
                position: 'relative',
                zIndex: 1
              }}>
                Learn from professionals with 10+ years of industry experience who bring real-world insights to every session
              </p>
            </div>

            <div className="feature-box" style={{
              background: 'linear-gradient(135deg, rgba(0, 61, 122, 0.95) 0%, rgba(0, 49, 93, 0.95) 100%)',
              padding: '1.8rem 1.5rem',
              borderRadius: '16px',
              minHeight: '130px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
            }}
            >
              <div className="glossy-shine" style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '50px',
                height: '200%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                pointerEvents: 'none',
                zIndex: 2
              }}></div>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.08) 100%)',
                pointerEvents: 'none'
              }}></div>
              <h3 style={{
                color: 'white',
                fontSize: 'clamp(1.1rem, 2.5vw, 1.25rem)',
                fontWeight: '700',
                marginBottom: '0.7rem',
                position: 'relative',
                zIndex: 1
              }}>
                100% Placement Support
              </h3>
              <p style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                lineHeight: '1.5',
                margin: 0,
                position: 'relative',
                zIndex: 1
              }}>
                Dedicated placement team with strong industry connections ensures career opportunities for every student
              </p>
            </div>

            <div className="feature-box" style={{
              background: 'linear-gradient(135deg, rgba(0, 61, 122, 0.95) 0%, rgba(0, 49, 93, 0.95) 100%)',
              padding: '1.8rem 1.5rem',
              borderRadius: '16px',
              minHeight: '130px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.25)';
              const shine = e.currentTarget.querySelector('.glossy-shine');
              if (shine) shine.style.animation = 'glossyShine 0.8s ease-out';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
              const shine = e.currentTarget.querySelector('.glossy-shine');
              if (shine) shine.style.animation = 'none';
            }}
            >
              <div className="glossy-shine" style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '50px',
                height: '200%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                pointerEvents: 'none',
                zIndex: 2
              }}></div>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.08) 100%)',
                pointerEvents: 'none'
              }}></div>
              <h3 style={{
                color: 'white',
                fontSize: 'clamp(1.1rem, 2.5vw, 1.25rem)',
                fontWeight: '700',
                marginBottom: '0.7rem',
                position: 'relative',
                zIndex: 1
              }}>
                Hands-On Project Training
              </h3>
              <p style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                lineHeight: '1.5',
                margin: 0,
                position: 'relative',
                zIndex: 1
              }}>
                Work on live projects and build a professional portfolio that showcases your skills to employers
              </p>
            </div>

            <div className="feature-box" style={{
              background: 'linear-gradient(135deg, rgba(0, 61, 122, 0.95) 0%, rgba(0, 49, 93, 0.95) 100%)',
              padding: '1.8rem 1.5rem',
              borderRadius: '16px',
              minHeight: '130px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
            }}
            >
              <div className="glossy-shine" style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '50px',
                height: '200%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                pointerEvents: 'none',
                zIndex: 2
              }}></div>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.08) 100%)',
                pointerEvents: 'none'
              }}></div>
              <h3 style={{
                color: 'white',
                fontSize: 'clamp(1.1rem, 2.5vw, 1.25rem)',
                fontWeight: '700',
                marginBottom: '0.7rem',
                position: 'relative',
                zIndex: 1
              }}>
                Updated Curriculum
              </h3>
              <p style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                lineHeight: '1.5',
                margin: 0,
                position: 'relative',
                zIndex: 1
              }}>
                Course content aligned with latest industry trends and technologies to keep you ahead in your career
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: FAQ */}
      <section className="content-section fade-section" style={{
        padding: '3rem 2rem',
        background: '#f9fafb'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            color: '#001f3f',
            marginBottom: '2rem',
            textAlign: 'center',
            fontWeight: '700'
          }}>
            Frequently Asked Questions
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {faqs.map((faq, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 20px rgba(0, 31, 63, 0.12), 0 2px 8px rgba(0, 31, 63, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  transform: activeFAQ === index ? 'translateY(-2px)' : 'translateY(0)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <button
                  onClick={() => setActiveFAQ(activeFAQ === index ? null : index)}
                  style={{
                    width: '100%',
                    padding: '1.1rem 1.5rem',
                    background: activeFAQ === index ? 'linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%)' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 'clamp(0.95rem, 2vw, 1rem)',
                    fontWeight: '600',
                    color: '#001f3f',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                  onMouseEnter={(e) => {
                    if (activeFAQ !== index) {
                      e.currentTarget.style.background = 'rgba(240, 247, 255, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeFAQ !== index) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span>{faq.question}</span>
                  <span style={{
                    fontSize: '1.8rem',
                    color: '#ff6b35',
                    fontWeight: 'bold',
                    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: activeFAQ === index ? 'rotate(45deg)' : 'rotate(0)',
                    flexShrink: 0,
                    marginLeft: '1rem',
                    display: 'inline-block'
                  }}>
                    +
                  </span>
                </button>
                {activeFAQ === index && (
                  <div style={{
                    padding: '0 1.5rem 1.2rem 1.5rem',
                    color: '#4b5563',
                    lineHeight: '1.7',
                    fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                    animation: 'fadeIn 0.4s ease-out',
                    borderTop: '1px solid rgba(0, 31, 63, 0.08)',
                    marginTop: '0.5rem',
                    paddingTop: '0.8rem'
                  }}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>


    </div>
  );
};

export default AboutUs;
